import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import companyApi from "../api/companyApi";
import useProductionWorkflow from "./useProductionWorkflow";
import {
  buildDefaultProductionMetricEntries,
  buildProductionMetricEntriesFromValues,
  buildProductionMetricOptions,
} from "../components/companies/productionUtils";
import { defaultProductionReportForm } from "../components/companies/productionDialogConstants";

export default function useProductionManagement({
  productionMetrics,
  setDeleteFeedback,
  onProductionReportSaved,
  onProductionReportDeleted,
  defaultReportingCurrency,
  assets,
  resourceReservesByAsset,
}) {
  const productionMetricRowId = useRef(0);
  const resolvedCurrency = defaultReportingCurrency || "USD";
  const assetCurrencyById = useMemo(() => {
    const map = new Map();
    (assets || []).forEach((asset) => {
      if (!asset?.id) return;
      map.set(asset.id, asset.reporting_currency || resolvedCurrency);
    });
    return map;
  }, [assets, resolvedCurrency]);
  const reserveCurrencyById = useMemo(() => {
    const map = new Map();
    Object.values(resourceReservesByAsset || {}).forEach((reserves) => {
      (reserves || []).forEach((reserve) => {
        const reserveId = reserve?.id;
        const assetId = reserve?.asset?.id || reserve?.asset_id || reserve?.asset;
        if (!reserveId) return;
        const currency = assetCurrencyById.get(assetId) || resolvedCurrency;
        map.set(reserveId, currency);
      });
    });
    return map;
  }, [assetCurrencyById, resourceReservesByAsset, resolvedCurrency]);
  const [addProductionReportOpen, setAddProductionReportOpen] = useState(false);
  const [productionReportForm, setProductionReportForm] = useState({
    ...defaultProductionReportForm,
    reporting_currency: resolvedCurrency,
  });
  const [productionReportSaveState, setProductionReportSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [productionMetricEntries, setProductionMetricEntries] = useState([]);
  const [showAddMetricSelector, setShowAddMetricSelector] = useState(false);
  const [productionReportDialogMode, setProductionReportDialogMode] = useState("create");
  const [productionReportDeleteTarget, setProductionReportDeleteTarget] = useState(null);
  const [productionReportDuplicateNotice, setProductionReportDuplicateNotice] = useState(null);

  useEffect(() => {
    if (!resolvedCurrency) return;
    setProductionReportForm((prev) => {
      if (prev.report_id) return prev;
      return { ...prev, reporting_currency: resolvedCurrency };
    });
  }, [resolvedCurrency]);

  const createMetricEntry = useCallback(
    (entry = {}, isAddRow = false) => ({
      row_id: `pm-${productionMetricRowId.current++}`,
      metric_id: "",
      commodity_id: "",
      value: "",
      isAddRow,
      ...entry,
    }),
    []
  );

  const ensureAddMetricRow = useCallback(
    (entries) => {
      if (entries.some((entry) => entry.isAddRow)) {
        return entries;
      }
      return [...entries, createMetricEntry({}, true)];
    },
    [createMetricEntry]
  );

  const attachRowIds = useCallback(
    (entries) => entries.map((entry) => createMetricEntry(entry, false)),
    [createMetricEntry]
  );

  const productionMetricOptions = useMemo(
    () => buildProductionMetricOptions(productionMetrics),
    [productionMetrics]
  );

  const { submitProductionReport, updateProductionReport } = useProductionWorkflow({
    companyApi,
    productionReportForm,
    productionMetricEntries,
    productionMetrics,
    setProductionReportSaveState,
    setAddProductionReportOpen,
    setProductionReportDialogMode,
    onProductionReportSaved,
  });

  const handleOpenAddProductionReport = (reserveId) => {
    const assetCurrency = reserveCurrencyById.get(reserveId) || resolvedCurrency;
    setProductionReportForm({
      ...defaultProductionReportForm,
      resource_reserve_id: reserveId,
      reporting_currency: assetCurrency,
    });
    setProductionReportSaveState({ loading: false, error: null, success: null });
    const entries = attachRowIds(buildDefaultProductionMetricEntries(productionMetrics));
    setProductionMetricEntries(ensureAddMetricRow(entries));
    setProductionReportDialogMode("create");
    setShowAddMetricSelector(false);
    setProductionReportDuplicateNotice(null);
    setAddProductionReportOpen(true);
  };

  const handleOpenEditProductionReport = (report, reserveId) => {
    if (!report) return;
    setProductionReportDuplicateNotice(null);
    const period = report.period || {};
    setProductionReportForm({
      report_id: report.id,
      resource_reserve_id: reserveId,
      period_type: period.period_type || "year",
      as_of_date: period.as_of_date || "",
      fiscal_year: period.fiscal_year || period.calendar_year || "",
      fiscal_quarter: period.fiscal_quarter || period.calendar_quarter || "",
      reporting_currency: report.reporting_currency || "USD",
      source_title: report.source_title || "",
      source_url: report.source_url || "",
      source_notes: report.source_notes || "",
    });
    const entries = attachRowIds(
      buildProductionMetricEntriesFromValues(report.values || [], productionMetrics)
    );
    setProductionMetricEntries(ensureAddMetricRow(entries));
    setProductionReportSaveState({ loading: false, error: null, success: null });
    setProductionReportDialogMode("edit");
    setShowAddMetricSelector(false);
    setAddProductionReportOpen(true);
  };

  const handleDuplicateProductionReport = (report, reserveId) => {
    if (!report) return;
    const period = report.period || {};
    setProductionReportForm({
      report_id: "",
      resource_reserve_id: reserveId,
      period_type: period.period_type || "year",
      as_of_date: "",
      fiscal_year: "",
      fiscal_quarter: "",
      reporting_currency: report.reporting_currency || "USD",
      source_title: "",
      source_url: "",
      source_notes: "",
    });
    const entries = attachRowIds(
      buildProductionMetricEntriesFromValues(report.values || [], productionMetrics)
    );
    setProductionMetricEntries(ensureAddMetricRow(entries));
    setProductionReportSaveState({ loading: false, error: null, success: null });
    setProductionReportDialogMode("create");
    setShowAddMetricSelector(false);
    setProductionReportDuplicateNotice("Report copied. Save to create a new draft.");
    setAddProductionReportOpen(true);
  };

  const handleCloseProductionReportDialog = () => {
    setAddProductionReportOpen(false);
    setProductionReportDialogMode("create");
    setProductionReportForm({
      ...defaultProductionReportForm,
      reporting_currency: resolvedCurrency,
    });
    setProductionMetricEntries([]);
    setShowAddMetricSelector(false);
    setProductionReportDuplicateNotice(null);
  };

  const handleProductionReportFieldChange = (field) => (event) => {
    const value = event.target.value;
    setProductionReportForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "period_type" && value === "year" ? { fiscal_quarter: "" } : null),
      ...(field === "resource_reserve_id"
        ? { reporting_currency: reserveCurrencyById.get(value) || resolvedCurrency }
        : null),
    }));
  };


  const handleProductionMetricEntryChange = (rowId, field) => (event) => {
    const value = event.target.value;
    setProductionMetricEntries((prev) =>
      prev.map((entry) => (entry.row_id === rowId ? { ...entry, [field]: value } : entry))
    );
  };

  const handleSelectProductionMetric = (rowId) => (_event, metric) => {
    setProductionMetricEntries((prev) => {
      const target = prev.find((entry) => entry.row_id === rowId);
      let updated = prev.map((entry) => {
        if (entry.row_id !== rowId) return entry;
        return {
          ...entry,
          metric_id: metric?.id || "",
          commodity_id: "",
          value: "",
          isAddRow: false,
        };
      });
      if (metric && target?.isAddRow) {
        updated = ensureAddMetricRow(updated);
        setShowAddMetricSelector(false);
      }
      return updated;
    });
  };

  const handleRemoveProductionMetric = (rowId) => {
    setProductionMetricEntries((prev) => prev.filter((entry) => entry.row_id !== rowId));
  };

  const handleConfirmDeleteProductionReport = useCallback(async () => {
    if (!productionReportDeleteTarget) return;
    const { reportId, reserveId } = productionReportDeleteTarget;
    try {
      const result = await companyApi.deleteProductionReport(reportId);
      setProductionReportDeleteTarget(null);
      if (onProductionReportDeleted) {
        onProductionReportDeleted(reserveId, reportId, result);
      }
      if (setDeleteFeedback) {
        setDeleteFeedback(
          result?.status === "delete_requested"
            ? "Production report deletion requested."
            : "Production report deleted."
        );
      }
    } catch {
      setProductionReportDeleteTarget(null);
      if (setDeleteFeedback) {
        setDeleteFeedback("Unable to delete production report.");
      }
    }
  }, [
    onProductionReportDeleted,
    productionReportDeleteTarget,
    setDeleteFeedback,
  ]);

  return {
    addProductionReportOpen,
    productionReportForm,
    productionReportSaveState,
    productionMetricEntries,
    productionMetricOptions,
    showAddMetricSelector,
    productionReportDialogMode,
    productionReportDeleteTarget,
    productionReportDuplicateNotice,
    setProductionReportDeleteTarget,
    handleOpenAddProductionReport,
    handleDuplicateProductionReport,
    handleOpenEditProductionReport,
    handleCloseProductionReportDialog,
    handleProductionReportFieldChange,
    handleSelectProductionMetric,
    handleProductionMetricEntryChange,
    handleRemoveProductionMetric,
    handleToggleAddMetricSelector: () => setShowAddMetricSelector(true),
    submitProductionReport,
    updateProductionReport,
    handleConfirmDeleteProductionReport,
  };
}

